import { useEffect, useMemo, useState } from 'react'
import { Plus, Pencil, List, ScanLine, FileJson, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { MenuPhotoImportModal } from './MenuPhotoImportModal'
import { MenuJsonImportModal } from './MenuJsonImportModal'
import { Skeleton } from '@/components/ui/Skeleton'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { MenuItemFormModal } from './MenuItemFormModal'
import { MenuImageField } from './MenuImageField'
import { POSVisualKeyboardField } from './POSVisualInput'
import {
  useMenuCategories,
  useMenuItems,
  useCreateMenuCategory,
  useUpdateMenuCategory,
  useDeleteMenuCategory,
  useSetItemAvailability,
  useDeleteMenuItem,
  useCreateDietaryTag,
  useDeleteDietaryTag,
  useDietaryTags,
  useEnsureDefaultDietaryTags,
  useUpdateDietaryTag,
} from '@/lib/queries/usePOS'
import { useUIStore } from '@/lib/store/useUIStore'
import { formatPOSMoney, usePOSCurrency } from '@/lib/pos/currency'
import { resolveMenuCategoryImageUrl, resolveMenuItemImageUrl } from '@/lib/pos/images'
import { searchMenuItems } from '@/lib/pos/menuSearch'
import type { DietaryTag, MenuCategory, MenuItem } from '@/lib/types/pos'

const EMPTY_MENU_CATEGORIES: MenuCategory[] = []
const EMPTY_MENU_ITEMS: MenuItem[] = []

function CategoryFormContent({
  onClose,
  category,
}: {
  onClose: () => void
  category?: MenuCategory | null
}) {
  const [name, setName] = useState(category?.name ?? '')
  const [description, setDescription] = useState(category?.description ?? '')
  const [imageUrl, setImageUrl] = useState(category?.imageUrl ?? '')
  const createCategory = useCreateMenuCategory()
  const updateCategory = useUpdateMenuCategory()
  const isEdit = !!category

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    if (isEdit) {
      await updateCategory.mutateAsync({
        documentId: category!.documentId,
        name: name.trim(),
        description: description.trim() || '',
        imageUrl: imageUrl.trim() || null,
      })
    } else {
      await createCategory.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        imageUrl: imageUrl.trim() || null,
      })
    }
    setName('')
    setDescription('')
    setImageUrl('')
    onClose()
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <POSVisualKeyboardField
        label="Name"
        value={name}
        onChange={setName}
      />
      <POSVisualKeyboardField
        label="Description"
        value={description}
        onChange={setDescription}
        multiline
      />
      <MenuImageField
        label="Category image"
        value={imageUrl}
        onChange={setImageUrl}
        imageClassName="h-32"
      />
      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
        <Button
          type="submit"
          size="sm"
          loading={createCategory.isPending || updateCategory.isPending}
          disabled={!name.trim()}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </div>
    </form>
  )
}

function CategoryFormModal({
  open,
  onClose,
  category,
}: {
  open: boolean
  onClose: () => void
  category?: MenuCategory | null
}) {
  const isEdit = !!category

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'Edit Category' : 'New Category'} size="sm">
      <CategoryFormContent
        key={category?.documentId ?? 'new'}
        onClose={onClose}
        category={category}
      />
    </Modal>
  )
}

function DeleteCategoryModal({
  open,
  category,
  categories,
  itemCount,
  targetCategoryDocumentId,
  loading,
  onClose,
  onTargetCategoryDocumentIdChange,
  onConfirm,
}: {
  open: boolean
  category: MenuCategory | null
  categories: MenuCategory[]
  itemCount: number
  targetCategoryDocumentId: string
  loading: boolean
  onClose: () => void
  onTargetCategoryDocumentIdChange: (documentId: string) => void
  onConfirm: (targetCategoryDocumentId: string | null) => void
}) {
  const availableTargets = useMemo(
    () => categories.filter((cat) => cat.documentId !== category?.documentId),
    [categories, category?.documentId],
  )

  return (
    <Modal open={open} onClose={onClose} title="Delete Category" size="sm">
      <div className="space-y-4">
        <div className="space-y-1">
          <p className="text-sm text-[var(--color-text-primary)]">
            Delete "{category?.name}"?
          </p>
          <p className="text-xs text-[var(--color-text-muted)]">
            {itemCount > 0
              ? `${itemCount} item${itemCount === 1 ? '' : 's'} currently use this category. Choose where they should go.`
              : 'No items use this category.'}
          </p>
        </div>

        {itemCount > 0 && (
          <label className="block space-y-1">
            <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
              Move items to
            </span>
            <select
              value={targetCategoryDocumentId}
              onChange={(event) => onTargetCategoryDocumentIdChange(event.target.value)}
              className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 text-sm text-[var(--color-text-primary)] outline-none transition-colors focus:border-[var(--color-accent-amber)]"
            >
              <option value="">Uncategorized</option>
              {availableTargets.map((cat) => (
                <option key={cat.documentId} value={cat.documentId}>
                  {cat.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            loading={loading}
            onClick={() => onConfirm(itemCount > 0 ? targetCategoryDocumentId || null : null)}
            className="bg-[var(--color-accent-red)] text-white hover:bg-[var(--color-accent-red)]/90"
          >
            Delete
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function DietaryTagsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: tagsResp, isLoading } = useDietaryTags()
  const tags: DietaryTag[] = (tagsResp as any)?.data ?? []
  const ensureDefaults = useEnsureDefaultDietaryTags()
  const createTag = useCreateDietaryTag()
  const updateTag = useUpdateDietaryTag()
  const deleteTag = useDeleteDietaryTag()

  const [name, setName] = useState('')
  const [icon, setIcon] = useState('AlertTriangle')
  const [color, setColor] = useState('#B7791F')
  const [defaultsRequested, setDefaultsRequested] = useState(false)

  useEffect(() => {
    if (!open) {
      setDefaultsRequested(false)
      return
    }
    if (!defaultsRequested && !isLoading && tags.length === 0 && !ensureDefaults.isPending) {
      setDefaultsRequested(true)
      ensureDefaults.mutate(tags)
    }
  }, [open, defaultsRequested, isLoading, tags, ensureDefaults])

  const resetForm = () => {
    setName('')
    setIcon('AlertTriangle')
    setColor('#B7791F')
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!name.trim()) return
    await createTag.mutateAsync({
      name: name.trim(),
      icon: icon.trim() || 'AlertTriangle',
      color: color.trim() || '#B7791F',
      sortOrder: tags.length * 10 + 100,
    })
    resetForm()
  }

  return (
    <Modal open={open} onClose={onClose} title="Dietary & allergies" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-[var(--color-text-muted)]">
          Create reusable allergy or dietary tags, then assign them to menu items from the item editor.
        </p>

        <form onSubmit={submit} className="grid gap-2 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3 md:grid-cols-[1fr_160px_96px_auto]">
          <POSVisualKeyboardField
            label="Name"
            value={name}
            onChange={setName}
            placeholder="e.g. Egg"
            inputClassName="h-9 text-sm"
          />
          <POSVisualKeyboardField
            label="Icon"
            value={icon}
            onChange={setIcon}
            placeholder="e.g. Egg"
            inputClassName="h-9 text-sm"
          />
          <label className="space-y-1 text-xs font-medium text-[var(--color-text-muted)]">
            <span>Color</span>
            <input
              type="color"
              value={color}
              onChange={(event) => setColor(event.target.value)}
              className="h-9 w-full rounded-[var(--radius-sm)] border border-[var(--color-border-default)] bg-transparent"
            />
          </label>
          <div className="flex items-end">
            <Button type="submit" size="sm" loading={createTag.isPending} disabled={!name.trim()}>
              <Plus className="h-4 w-4" />
              Add
            </Button>
          </div>
        </form>

        <div className="space-y-2">
          {isLoading || ensureDefaults.isPending ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} variant="text" />
              ))}
            </div>
          ) : tags.length === 0 ? (
            <div className="rounded-[var(--radius-md)] border border-dashed border-[var(--color-border-default)] p-5 text-center">
              <p className="text-sm text-[var(--color-text-muted)]">No dietary tags yet.</p>
              <Button type="button" size="sm" className="mt-3" onClick={() => ensureDefaults.mutate(tags)}>
                Create defaults
              </Button>
            </div>
          ) : (
            tags.map((tag) => (
              <div
                key={tag.documentId}
                className="grid gap-2 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-3 md:grid-cols-[1fr_160px_96px_auto]"
              >
                <POSVisualKeyboardField
                  value={tag.name}
                  onChange={(value) => updateTag.mutate({ documentId: tag.documentId, name: value })}
                  inputClassName="h-8 text-sm"
                />
                <POSVisualKeyboardField
                  value={tag.icon}
                  onChange={(value) => updateTag.mutate({ documentId: tag.documentId, icon: value || 'AlertTriangle' })}
                  inputClassName="h-8 text-sm"
                />
                <input
                  type="color"
                  value={tag.color || '#B7791F'}
                  onChange={(event) => updateTag.mutate({ documentId: tag.documentId, color: event.target.value })}
                  className="h-8 w-full rounded-[var(--radius-sm)] border border-[var(--color-border-default)] bg-transparent"
                  aria-label={`${tag.name} color`}
                />
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold text-white"
                    style={{ backgroundColor: tag.color || '#B7791F' }}
                  >
                    {tag.name}
                  </span>
                  <button
                    type="button"
                    aria-label={`Delete ${tag.name}`}
                    onClick={() => {
                      if (confirm(`Delete dietary tag "${tag.name}"?`)) deleteTag.mutate(tag.documentId)
                    }}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-muted)] transition-colors hover:bg-red-500/10 hover:text-[var(--color-accent-red)]"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  )
}

const STATION_COLOR: Record<string, string> = {
  kitchen: 'bg-orange-500/10 text-orange-400',
  bar: 'bg-blue-500/10 text-blue-400',
  grill: 'bg-red-500/10 text-red-400',
  cold: 'bg-cyan-500/10 text-cyan-400',
  none: 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]',
}

export function POSMenuView() {
  const selectedCategoryId = useUIStore((s) => s.posMenuSelectedCategoryId)
  const setSelectedCategoryId = useUIStore((s) => s.setPosMenuSelectedCategoryId)
  const currency = usePOSCurrency()
  const money = useMemo(() => (amount: number) => formatPOSMoney(amount, currency), [currency])

  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<MenuCategory | null>(null)
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null)
  const [deletingCategory, setDeletingCategory] = useState<MenuCategory | null>(null)
  const [deleteTargetCategoryDocumentId, setDeleteTargetCategoryDocumentId] = useState('')
  const [showItemModal, setShowItemModal] = useState(false)
  const [showPhotoImport, setShowPhotoImport] = useState(false)
  const [showJsonImport, setShowJsonImport] = useState(false)
  const [showDietaryTags, setShowDietaryTags] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const { data: catsResp, isLoading: catsLoading } = useMenuCategories()
  const categories: MenuCategory[] = (catsResp as { data?: MenuCategory[] } | undefined)?.data ?? EMPTY_MENU_CATEGORIES

  const { data: itemsResp, isLoading: itemsLoading } = useMenuItems(selectedCategoryId)
  const items: MenuItem[] = (itemsResp as { data?: MenuItem[] } | undefined)?.data ?? EMPTY_MENU_ITEMS
  const { data: allItemsResp, isLoading: allItemsLoading } = useMenuItems()
  const allItems: MenuItem[] = (allItemsResp as { data?: MenuItem[] } | undefined)?.data ?? EMPTY_MENU_ITEMS
  const isSearching = searchQuery.trim().length > 0
  const displayedItems = isSearching ? allItems : items
  const displayedItemsLoading = isSearching ? allItemsLoading : itemsLoading
  const deletingCategoryItemCount = useMemo(
    () =>
      deletingCategory
        ? allItems.filter((item) => item.category?.documentId === deletingCategory.documentId).length
        : 0,
    [allItems, deletingCategory],
  )

  const filteredItems = useMemo(() => {
    return isSearching ? searchMenuItems(allItems, searchQuery) : items
  }, [allItems, isSearching, items, searchQuery])

  const setAvailability = useSetItemAvailability()
  const deleteItem = useDeleteMenuItem()
  const deleteCategory = useDeleteMenuCategory()

  const openCreate = () => {
    setEditingItem(null)
    setShowItemModal(true)
  }

  const openEdit = (item: MenuItem) => {
    setEditingItem(item)
    setShowItemModal(true)
  }

  const confirmDeleteCategory = async (targetCategoryDocumentId: string | null) => {
    if (!deletingCategory) return
    const deletedCategoryDocumentId = deletingCategory.documentId
    await deleteCategory.mutateAsync({
      documentId: deletedCategoryDocumentId,
      reassignToCategoryDocumentId: targetCategoryDocumentId,
    })
    if (selectedCategoryId === deletedCategoryDocumentId) {
      setSelectedCategoryId(targetCategoryDocumentId ?? null)
    }
    setDeletingCategory(null)
    setDeleteTargetCategoryDocumentId('')
  }

  return (
    <div className="flex h-full gap-0">
      {/* Category sidebar */}
      <div className="w-56 shrink-0 border-r border-[var(--color-border-subtle)] flex flex-col">
        <div className="flex items-center justify-between px-3 py-3 border-b border-[var(--color-border-subtle)]">
          <span className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wide">
            Categories
          </span>
          <button
            onClick={() => {
              setEditingCategory(null)
              setShowCategoryModal(true)
            }}
            className="h-6 w-6 flex items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-1">
          {catsLoading ? (
            <div className="space-y-1 px-2 py-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="text" />
              ))}
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => {
                  setSelectedCategoryId(null)
                  if (isSearching) setSearchQuery('')
                }}
                className={[
                  'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-[var(--radius-sm)] transition-colors text-left',
                  selectedCategoryId === null || isSearching
                    ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)]/50',
                ].join(' ')}
              >
                <List className="h-3.5 w-3.5 shrink-0" />
                All Items
                <span className="ml-auto text-xs text-[var(--color-text-muted)]">{allItems.length}</span>
              </button>

              {categories.map((cat: MenuCategory) => {
                const categoryImage = resolveMenuCategoryImageUrl(cat)
                return (
                <div
                  key={cat.documentId}
                  className={[
                    'group flex w-full items-center gap-2 rounded-[var(--radius-sm)] text-sm transition-colors',
                    selectedCategoryId === cat.documentId && !isSearching
                      ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]'
                      : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)]/50',
                  ].join(' ')}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCategoryId(cat.documentId)
                      if (isSearching) setSearchQuery('')
                    }}
                    className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left"
                  >
                    {categoryImage && (
                      <img
                        src={categoryImage}
                        alt=""
                        className="h-7 w-7 shrink-0 rounded-[var(--radius-sm)] object-cover"
                      />
                    )}
                    <span className="block truncate">{cat.name}</span>
                  </button>
                  <button
                    type="button"
                    aria-label={`Edit category ${cat.name}`}
                    title="Edit category"
                    onClick={(e) => {
                      e.stopPropagation()
                      setEditingCategory(cat)
                      setShowCategoryModal(true)
                    }}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete category ${cat.name}`}
                    title="Delete category"
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeleteTargetCategoryDocumentId('')
                      setDeletingCategory(cat)
                    }}
                    className="mr-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-muted)] transition-colors hover:bg-red-500/10 hover:text-[var(--color-accent-red)]"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                )
              })}
            </>
          )}
        </div>
      </div>

      {/* Items area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border-subtle)]">
          <h3 className="text-sm font-medium text-[var(--color-text-primary)]">
            {selectedCategoryId
              ? categories.find((c) => c.documentId === selectedCategoryId)?.name ?? 'Items'
              : 'All Items'}
          </h3>
          <div className="flex items-center gap-2">
            <div>
              <POSVisualKeyboardField
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search items or categories..."
                className="w-56"
                inputClassName="h-8 text-xs rounded-[var(--radius-md)]"
              />
            </div>
            <Button size="sm" variant="secondary" onClick={() => setShowJsonImport(true)}>
              <FileJson className="h-4 w-4 mr-1" />
              Import from JSON
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setShowDietaryTags(true)}>
              <List className="h-4 w-4 mr-1" />
              Dietary & allergies
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setShowPhotoImport(true)}>
              <ScanLine className="h-4 w-4 mr-1" />
              Import from photos
            </Button>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Add Item
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {displayedItemsLoading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={120} />
              ))}
            </div>
          ) : displayedItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-sm text-[var(--color-text-muted)]">
                {selectedCategoryId && !isSearching
                  ? 'No items in this category. Add your first item.'
                  : 'No menu items yet. Add your first item.'}
              </p>
              <Button size="sm" variant="secondary" className="mt-3" onClick={openCreate}>
                <Plus className="h-4 w-4" />
                Add Item
              </Button>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-sm text-[var(--color-text-muted)]">
                No items match "{searchQuery.trim()}".
              </p>
              <Button size="sm" variant="secondary" className="mt-3" onClick={() => setSearchQuery('')}>
                Clear search
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredItems.map((item) => {
                const itemImage = resolveMenuItemImageUrl(item)
                return (
                <div
                  key={item.documentId}
                  className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-3 flex flex-col gap-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    {itemImage && (
                      <img
                        src={itemImage}
                        alt=""
                        className="h-14 w-14 shrink-0 rounded-[var(--radius-sm)] object-cover"
                      />
                    )}
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${item.isAvailable ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-muted)]'}`}>
                        {item.name}
                      </p>
                      {item.description && (
                        <p className="text-xs text-[var(--color-text-muted)] truncate mt-0.5">
                          {item.description}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => openEdit(item)}
                      aria-label={`Edit ${item.name}`}
                      title="Edit item"
                      className="shrink-0 h-6 w-6 flex items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                      {money(item.basePrice)}
                    </span>
                    {item.activePromotion && (
                      <>
                        <span className="text-xs text-[var(--color-text-muted)] line-through">
                          {money(item.activePromotion.regularPrice)}
                        </span>
                        <Badge variant="amber">{item.activePromotion.label || 'Promo'}</Badge>
                      </>
                    )}
                    {item.category && (
                      <Badge>{item.category.name}</Badge>
                    )}
                    {item.orderRole === 'child_only' && (
                      <Badge variant="amber">Extra only</Badge>
                    )}
                    {(item.dietaryTags ?? []).map((tag) => (
                      <span
                        key={tag.documentId}
                        className="rounded-full px-1.5 py-0.5 text-xs font-medium text-white"
                        style={{ backgroundColor: tag.color || '#B7791F' }}
                      >
                        {tag.name}
                      </span>
                    ))}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${STATION_COLOR[item.prepStation]}`}>
                      {item.prepStation}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <div
                        role="switch"
                        aria-checked={item.isAvailable}
                        aria-label={`${item.name} availability`}
                        onClick={() => setAvailability.mutate({ documentId: item.documentId, isAvailable: !item.isAvailable })}
                        className={[
                          'relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer',
                          item.isAvailable ? 'bg-[var(--color-accent-amber)]' : 'bg-[var(--color-bg-elevated)] border border-[var(--color-border-default)]',
                        ].join(' ')}
                      >
                        <span
                          className={[
                            'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                            item.isAvailable ? 'translate-x-4' : 'translate-x-0.5',
                          ].join(' ')}
                        />
                      </div>
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {item.isAvailable ? 'Available' : 'Unavailable'}
                      </span>
                    </label>
                    {item.variants.length > 0 && (
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {item.variants.length} variant{item.variants.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    <button
                      type="button"
                      aria-label={`Delete ${item.name}`}
                      title="Delete item"
                      onClick={() => {
                        if (confirm(`Delete item "${item.name}"?`)) deleteItem.mutate(item.documentId)
                      }}
                      className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-muted)] transition-colors hover:bg-red-500/10 hover:text-[var(--color-accent-red)]"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      <CategoryFormModal
        open={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false)
          setEditingCategory(null)
        }}
        category={editingCategory}
      />
      <MenuItemFormModal
        open={showItemModal}
        onClose={() => { setShowItemModal(false); setEditingItem(null) }}
        item={editingItem}
        defaultCategoryDocumentId={selectedCategoryId}
      />
      <MenuPhotoImportModal open={showPhotoImport} onClose={() => setShowPhotoImport(false)} />
      <MenuJsonImportModal open={showJsonImport} onClose={() => setShowJsonImport(false)} />
      <DietaryTagsModal open={showDietaryTags} onClose={() => setShowDietaryTags(false)} />
      <DeleteCategoryModal
        open={!!deletingCategory}
        category={deletingCategory}
        categories={categories}
        itemCount={deletingCategoryItemCount}
        targetCategoryDocumentId={deleteTargetCategoryDocumentId}
        loading={deleteCategory.isPending}
        onClose={() => {
          if (!deleteCategory.isPending) {
            setDeletingCategory(null)
            setDeleteTargetCategoryDocumentId('')
          }
        }}
        onTargetCategoryDocumentIdChange={setDeleteTargetCategoryDocumentId}
        onConfirm={confirmDeleteCategory}
      />
    </div>
  )
}
